import React from "react";
import { Popover } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { ReactComponent as MergeIcon } from "../../../assets/svg/merge-icon.svg";
import { GRAY_100, LIGHT_PURPLE, PRIMARY_700, PRIMARY_800, PURPLE_HEART } from "../../../constant/colors";
import { Routes } from "../../../constant/routes";
import { fontWeights, sizes } from "../../../constant/styles";
import { useGetSetting } from "../../../hooks/getSetting";
import { formatPatientDisplayId } from "../../../utils/formaters";

const POPOVER_MAX_WIDTH = 240;

const POPOVER_MIN_WIDTH = 96;

const PILL_FONT_SIZE = sizes.small;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 12px;
  background: ${LIGHT_PURPLE};
  color: ${PURPLE_HEART};
  font-size: ${PILL_FONT_SIZE};
  font-weight: ${fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: default;
`;

const StyledMergeIcon = styled(MergeIcon)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const MergedPatientRow = styled.div`
  a {
    display: block;
    font-size: ${sizes.small};
    font-weight: ${fontWeights.regular};
    color: ${PRIMARY_700};
    overflow-wrap: anywhere;
    text-align: center;

    &:hover,
    &:focus {
      color: ${PRIMARY_800};
      text-decoration: underline;
    }
  }
`;

const MergedPatientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: ${POPOVER_MIN_WIDTH}px;
  max-width: ${POPOVER_MAX_WIDTH}px;
  /* Many merges shouldn't grow the card past the header — scroll instead. */
  max-height: 240px;
  overflow-y: auto;
`;

const ListDivider = styled.div`
  height: 1px;
  background: ${GRAY_100};
`;

export const PatientMergeTag = ({ mergedFromPatients }) => {
  const { t } = useTranslation();
  const [prefix] = useGetSetting(["id_prefix"]);

  if (!mergedFromPatients?.length) {
    return null;
  }

  const displayId = (id) => formatPatientDisplayId(prefix, id);

  const mergedPatientsList = (
    <MergedPatientsList>
      {mergedFromPatients.map(({ id }, index) => (
        <React.Fragment key={id}>
          {index > 0 && <ListDivider />}
          <MergedPatientRow>
            <Link to={Routes.PATIENT_PATH({ patientId: id })} id={`merged_patient_${id}`}>
              {displayId(id)}
            </Link>
          </MergedPatientRow>
        </React.Fragment>
      ))}
    </MergedPatientsList>
  );

  return (
    <Popover
      content={mergedPatientsList}
      placement="top"
      trigger={["hover", "focus"]}
      overlayInnerStyle={{ borderRadius: 10 }}
    >
      <Pill id="patient_merged_tag" tabIndex={0}>
        <StyledMergeIcon />
        {t("patientTopSection.merge.merged_count", { count: mergedFromPatients.length })}
      </Pill>
    </Popover>
  );
};
